/**
 * ScopeBreakdownCard — A2F-487 / A2F-488
 *
 * Summary card that shows two counts mapping 1:1 to the two options of
 * `ScopeFilterSelect`:
 *
 *   - thisSub    → "This sub only" (default Review filter)
 *   - allScopes  → "All scopes" (cumulative)
 *
 * The big number is `thisSub` because that is the page's default scope.
 * The smaller number below provides instant verification against the
 * scope dropdown without needing to click.
 *
 * A2F-488: Each number is also a filter shortcut. When the parent passes
 * `statusValue` + `onFilter`, each number becomes a button that sets both
 * the status filter and the scope filter on click. The currently-selected
 * cell (matching `activeStatus` + `activeScope`) gets a highlight ring so
 * users always see which cell of the filter matrix the table is
 * reflecting.
 *
 * A2F-761: The middle "this sub + prior" tile and corresponding 'in-scope'
 * filter were removed from the UI. `thisSubAndPrior` remains in the props
 * shape (still computed by the data layer) so the option can be reinstated
 * without a data-layer change.
 *
 * Used by all 5 Review pages. When there is no submission focus, pages
 * use the `<SummaryStatCard>` component instead (single-number variant).
 */

export type ScopeFilterValue = 'this-sub' | 'in-scope' | 'all';

type ColorKey = 'slate' | 'red' | 'orange' | 'blue' | 'green';

const BIG_NUMBER_COLOR: Record<ColorKey, string> = {
  slate: 'text-slate-700',
  red: 'text-red-600',
  orange: 'text-orange-600',
  blue: 'text-blue-600',
  green: 'text-green-600',
};

export interface ScopeBreakdownCardProps {
  label: string;
  color: ColorKey;
  thisSub: number;
  thisSubAndPrior: number;
  allScopes: number;

  /**
   * A2F-488: when provided together with `onFilter`, each number becomes
   * a filter shortcut. `statusValue` identifies which status this card
   * represents (e.g., 'undefined', 'conflicting'; 'all' for the Total
   * card that doesn't filter by status).
   */
  statusValue?: string;
  activeStatus?: string;
  activeScope?: ScopeFilterValue;
  onFilter?: (status: string, scope: ScopeFilterValue) => void;
}

export function ScopeBreakdownCard({
  label,
  color,
  thisSub,
  // A2F-761: thisSubAndPrior intentionally not destructured — it remains
  // in the props shape for forward/backward compatibility but is no longer
  // rendered. Callers can still pass it without modification.
  allScopes,
  statusValue,
  activeStatus,
  activeScope,
  onFilter,
}: ScopeBreakdownCardProps) {
  const clickable = onFilter != null && statusValue != null;

  const isActive = (scope: ScopeFilterValue) =>
    clickable && activeStatus === statusValue && activeScope === scope;

  const handleClick = (scope: ScopeFilterValue) => {
    if (onFilter && statusValue != null) {
      onFilter(statusValue, scope);
    }
  };

  const bigNumberActive = isActive('this-sub');
  const allScopesActive = isActive('all');

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col">
      <p className="text-xs text-slate-500 mb-1">{label}</p>

      {clickable ? (
        <button
          type="button"
          onClick={() => handleClick('this-sub')}
          aria-pressed={bigNumberActive}
          title={`Show ${label.toLowerCase()} in this sub`}
          className={`text-left -mx-2 px-2 py-1 rounded-md transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 ${
            bigNumberActive ? 'bg-orange-50 ring-2 ring-brand-orange/40' : ''
          }`}
        >
          <p className={`text-2xl font-bold leading-none ${BIG_NUMBER_COLOR[color]}`}>
            {thisSub}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">this sub</p>
        </button>
      ) : (
        <>
          <p className={`text-2xl font-bold leading-none ${BIG_NUMBER_COLOR[color]}`}>
            {thisSub}
          </p>
          <p className="text-[10px] text-slate-400 mt-0.5">this sub</p>
        </>
      )}

      <div className="border-t border-slate-100 mt-2 pt-2 space-y-0.5">
        {clickable ? (
          <button
            type="button"
            onClick={() => handleClick('all')}
            aria-pressed={allScopesActive}
            title={`Show ${label.toLowerCase()} in all scopes`}
            className={`w-full text-left -mx-2 px-2 py-0.5 rounded-md transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-brand-orange/40 ${
              allScopesActive ? 'bg-orange-50 ring-2 ring-brand-orange/40' : ''
            }`}
          >
            <p className="text-[10px] text-slate-500 flex items-baseline gap-1.5">
              <span className="font-semibold tabular-nums text-slate-600 min-w-[1.5rem]">
                {allScopes}
              </span>
              <span className="text-slate-400">all scopes</span>
            </p>
          </button>
        ) : (
          <p className="text-[10px] text-slate-500 flex items-baseline gap-1.5">
            <span className="font-semibold tabular-nums text-slate-600 min-w-[1.5rem]">
              {allScopes}
            </span>
            <span className="text-slate-400">all scopes</span>
          </p>
        )}
      </div>
    </div>
  );
}
